import React, { useState, useMemo } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { MapPin, Users, AlertTriangle, CheckCircle, X } from "lucide-react";

// US Atlas TopoJSON from CDN — standard, no install needed
const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// FIPS code → 2-letter state abbreviation
const FIPS_TO_STATE = {
  "01": "AL", "02": "AK", "04": "AZ", "05": "AR", "06": "CA",
  "08": "CO", "09": "CT", "10": "DE", "11": "DC", "12": "FL",
  "13": "GA", "15": "HI", "16": "ID", "17": "IL", "18": "IN",
  "19": "IA", "20": "KS", "21": "KY", "22": "LA", "23": "ME",
  "24": "MD", "25": "MA", "26": "MI", "27": "MN", "28": "MS",
  "29": "MO", "30": "MT", "31": "NE", "32": "NV", "33": "NH",
  "34": "NJ", "35": "NM", "36": "NY", "37": "NC", "38": "ND",
  "39": "OH", "40": "OK", "41": "OR", "42": "PA", "44": "RI",
  "45": "SC", "46": "SD", "47": "TN", "48": "TX", "49": "UT",
  "50": "VT", "51": "VA", "53": "WA", "54": "WV", "55": "WI",
  "56": "WY", "72": "PR"
};

// Distinct colors for up to 8 reps
const REP_COLORS = [
  "#2563EB", "#059669", "#D97706", "#7C3AED",
  "#DC2626", "#0891B2", "#65A30D", "#DB2777"
];
const MULTI_REP_COLOR = "#0D9488";   // teal — multiple reps share this state
const NO_COVERAGE_COLOR = "#E2E8F0"; // gray — no rep assigned
const HOVER_LIGHTEN = "opacity-80";

export default function TerritoryMapTab({ team }) {
  const [selectedState, setSelectedState] = useState(null); // { abbr, reps[] }
  const [tooltip, setTooltip] = useState(null); // { x, y, content }

  // Build rep color map (stable by user id order)
  const repColors = useMemo(() => {
    const activeReps = team.filter(u => u.active !== false && u.territories?.length > 0);
    const map = {};
    activeReps.forEach((rep, i) => {
      map[rep.id] = REP_COLORS[i % REP_COLORS.length];
    });
    return map;
  }, [team]);

  // Build state → reps mapping
  const stateCoverage = useMemo(() => {
    const coverage = {}; // { "TX": [rep1, rep2], ... }
    team.filter(u => u.active !== false).forEach(rep => {
      (rep.territories || []).forEach(t => {
        if (!coverage[t.state]) coverage[t.state] = [];
        if (!coverage[t.state].find(r => r.id === rep.id)) {
          coverage[t.state].push(rep);
        }
      });
    });
    return coverage;
  }, [team]);

  const getStateColor = (stateAbbr) => {
    const reps = stateCoverage[stateAbbr];
    if (!reps || reps.length === 0) return NO_COVERAGE_COLOR;
    if (reps.length > 1) return MULTI_REP_COLOR;
    return repColors[reps[0].id] || REP_COLORS[0];
  };

  const coveredCount = Object.keys(stateCoverage).length;
  const totalStates = 50;
  const gapCount = totalStates - coveredCount;

  const activeRepsWithTerritories = team.filter(u => u.active !== false && u.territories?.length > 0);

  return (
    <div className="space-y-5">
      {/* Coverage summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">States Covered</span>
            <CheckCircle className="w-4 h-4 text-green-500" />
          </div>
          <div className="font-heading text-3xl font-bold text-slate-800" data-testid="map-covered-count">{coveredCount}</div>
          <div className="text-xs text-slate-400 mt-1">of {totalStates} states</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Coverage Gaps</span>
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          </div>
          <div className="font-heading text-3xl font-bold text-slate-800" data-testid="map-gap-count">{gapCount}</div>
          <div className="text-xs text-slate-400 mt-1">states with no rep</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Active Reps</span>
            <Users className="w-4 h-4 text-navy" />
          </div>
          <div className="font-heading text-3xl font-bold text-slate-800" data-testid="map-rep-count">{activeRepsWithTerritories.length}</div>
          <div className="text-xs text-slate-400 mt-1">with territories assigned</div>
        </div>
      </div>

      <div className="flex gap-5">
        {/* Map */}
        <div className="flex-1 bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-700 text-sm">Territory Coverage</h3>
            <p className="text-xs text-slate-400">Click a state to see rep details</p>
          </div>

          <div className="relative" data-testid="territory-map">
            <ComposableMap
              projection="geoAlbersUsa"
              width={800}
              height={480}
              style={{ width: "100%", height: "auto" }}
            >
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const fips = geo.id?.toString().padStart(2, "0");
                    const stateAbbr = FIPS_TO_STATE[fips];
                    const fill = stateAbbr ? getStateColor(stateAbbr) : NO_COVERAGE_COLOR;
                    const reps = stateAbbr ? (stateCoverage[stateAbbr] || []) : [];

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={fill}
                        stroke="#FFFFFF"
                        strokeWidth={0.8}
                        style={{
                          default: { fill, outline: "none", cursor: "pointer" },
                          hover: { fill, opacity: 0.75, outline: "none", cursor: "pointer" },
                          pressed: { fill, opacity: 0.6, outline: "none" },
                        }}
                        onClick={() => {
                          if (stateAbbr) {
                            setSelectedState({ abbr: stateAbbr, reps });
                          }
                        }}
                        onMouseEnter={(evt) => {
                          if (stateAbbr) {
                            setTooltip({
                              x: evt.clientX,
                              y: evt.clientY,
                              state: stateAbbr,
                              reps,
                            });
                          }
                        }}
                        onMouseLeave={() => setTooltip(null)}
                        data-testid={`state-${stateAbbr}`}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>

            {/* Tooltip */}
            {tooltip && (
              <div
                className="fixed z-50 pointer-events-none bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg"
                style={{ left: tooltip.x + 12, top: tooltip.y - 10 }}
              >
                <div className="font-semibold">{tooltip.state}</div>
                {tooltip.reps.length === 0 ? (
                  <div className="text-slate-300 mt-0.5">No coverage</div>
                ) : tooltip.reps.length === 1 ? (
                  <div className="text-slate-300 mt-0.5">{tooltip.reps[0].name}</div>
                ) : (
                  <div className="text-teal-300 mt-0.5">{tooltip.reps.length} reps — click for details</div>
                )}
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-slate-100">
            {activeRepsWithTerritories.map(rep => (
              <div key={rep.id} className="flex items-center gap-1.5 text-xs text-slate-600">
                <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: repColors[rep.id] }}></span>
                {rep.name}
              </div>
            ))}
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <span className="w-3 h-3 rounded-sm flex-shrink-0 bg-teal-500"></span>
              Shared (multi-rep)
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <span className="w-3 h-3 rounded-sm flex-shrink-0 bg-slate-200"></span>
              No coverage
            </div>
          </div>
        </div>

        {/* State Detail Panel */}
        {selectedState && (
          <div className="w-72 bg-white border border-slate-200 rounded-xl p-5 flex-shrink-0 animate-slide-up" data-testid="state-detail-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-slate-800 text-lg">{selectedState.abbr}</h3>
                <p className="text-xs text-slate-400 mt-0.5">Territory detail</p>
              </div>
              <button onClick={() => setSelectedState(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            {selectedState.reps.length === 0 ? (
              <div className="text-center py-6">
                <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-slate-500 font-medium text-sm">No coverage</p>
                <p className="text-slate-400 text-xs mt-1">No rep assigned to {selectedState.abbr}</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="state-reps-list">
                {selectedState.reps.map(rep => {
                  const territory = rep.territories.find(t => t.state === selectedState.abbr);
                  return (
                    <div key={rep.id} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: repColors[rep.id] }}></span>
                        <div>
                          <p className="font-semibold text-slate-800 text-sm">{rep.name}</p>
                          <p className="text-xs text-slate-400">{rep.role === "lc_manager" ? "Manager" : "Field Staff"}</p>
                        </div>
                      </div>
                      {territory?.counties?.length > 0 && (
                        <div className="ml-5">
                          <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1">Counties</p>
                          <div className="flex flex-wrap gap-1">
                            {territory.counties.map(c => (
                              <span key={c} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{c}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {territory?.zips?.length > 0 && (
                        <div className="ml-5">
                          <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1">Zip Codes</p>
                          <div className="flex flex-wrap gap-1">
                            {territory.zips.slice(0, 8).map(z => (
                              <span key={z} className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-medium">{z}</span>
                            ))}
                            {territory.zips.length > 8 && (
                              <span className="text-xs text-slate-400">+{territory.zips.length - 8} more</span>
                            )}
                          </div>
                        </div>
                      )}
                      {(!territory?.counties?.length && !territory?.zips?.length) && (
                        <p className="ml-5 text-xs text-slate-400 italic">Covers entire state</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
